// variable-trace-chrome — the guided-make "Show my variables" readout (Trello lFu60Fgm).
//
// An ELEVENTH sibling chrome module, NOT uniformly wired: it ALLOW-LISTS `body[data-surface]` to
// exactly `guided-make` and returns before touching anything anywhere else. Wired LATE from
// main.js, after `surface.wire` (and after the test table) inside `loadProject().then()`, because
// it needs `core` (to read the live `main.py`) and the bundled Ace (its off-screen runner is a
// second editor-core, `hidden-runner.js`, reused as-is). So it is never wired on a LOCKED plan
// page — main.js returns before reaching it, the plan rail's own treatment.
//
// ON DEMAND ONLY: nothing runs until the student presses "Show my variables". Each press reads
// the student's CURRENT code, builds the bounded tracer program (`variable-trace.js`, pure) and
// runs it ONCE in the hidden runner under `runWithTimeout`'s budget — a blown budget stops and
// restarts ONLY the hidden runner, never the visible one, and the visible game is never touched.
// The hidden runner is created lazily on the first press, so a student who never asks pays
// nothing. editor-core.js / editor-api.js and the runner contract are unchanged.
//
// SAFETY: every program-derived string (variable names and values) is written with textContent
// only — this module never assigns innerHTML. Values were already truncated and stripped of
// control characters by `readTraceOutput`.

import { createHiddenRunner } from './hidden-runner.js';
import { buildTraceProgram, describeRun, COPY, RUN_BUDGET_MS } from './variable-trace.js';

var ALLOWED_SURFACES = ['guided-make'];

function el(doc, tag, cls, text) {
  var n = doc.createElement(tag);
  if (cls) { n.className = cls; }
  if (text != null) { n.textContent = String(text); }
  return n;
}

function readShowVariables(doc) {
  try {
    var node = doc.getElementById('guided-make-config');
    if (!node) { return false; }
    var cfg = JSON.parse(node.textContent || '{}');
    return !!cfg && cfg.showVariables === true;
  } catch (e) { return false; }
}

function clear(node) {
  while (node.firstChild) { node.removeChild(node.firstChild); }
}

/** A small (name, value) or (line, name, value) table. Every cell is textContent. */
function buildTable(doc, headers, rows) {
  var table = el(doc, 'table', 'gmv-table');
  var thead = el(doc, 'thead');
  var hr = el(doc, 'tr');
  headers.forEach(function (h) {
    var th = el(doc, 'th', null, h);
    th.setAttribute('scope', 'col');
    hr.appendChild(th);
  });
  thead.appendChild(hr);
  table.appendChild(thead);
  var tbody = el(doc, 'tbody');
  rows.forEach(function (cells) {
    var tr = el(doc, 'tr');
    cells.forEach(function (c, i) {
      tr.appendChild(el(doc, 'td', i === cells.length - 1 ? 'gmv-value' : 'gmv-name', c));
    });
    tbody.appendChild(tr);
  });
  table.appendChild(tbody);
  return table;
}

/**
 * Wire the readout. Returns the controller ({ run, hide }) or null when not applicable. Never throws.
 *   opts = { core, ace, doc, mode, createHiddenRunner? (test seam) }
 */
export function wireVariableTrace(opts) {
  try {
    var o = opts || {};
    var doc = o.doc || document;
    var core = o.core;
    var surface = doc.body ? (doc.body.getAttribute('data-surface') || 'editor') : 'editor';
    if (ALLOWED_SURFACES.indexOf(surface) === -1 || !core) { return null; }
    var panel = doc.getElementById('guided-make-panel');
    if (!panel) { return null; }
    // Opt-in per activity: the server ships `showVariables: true` in #guided-make-config only
    // when the teacher switched the readout on. Anything else (absent, false, unreadable) = no
    // section at all, so students are never offered a tool the activity doesn't use.
    if (!readShowVariables(doc)) { return null; }
    var mode = o.mode || doc.body.getAttribute('data-clientside-mode') || '';

    // ── the section ───────────────────────────────────────────────────────────────────────
    var section = el(doc, 'section', 'gmv');
    section.setAttribute('aria-labelledby', 'gmv-heading');
    var heading = el(doc, 'h2', 'gmv-heading', COPY.heading);
    heading.id = 'gmv-heading';
    var intro = el(doc, 'p', 'gmv-intro', COPY.intro);
    var controls = el(doc, 'div', 'gmv-controls');
    var showBtn = el(doc, 'button', 'gmv-btn', COPY.show);
    showBtn.type = 'button';
    showBtn.setAttribute('aria-expanded', 'false');
    showBtn.setAttribute('aria-controls', 'gmv-result');
    var hideBtn = el(doc, 'button', 'gmv-btn gmv-btn-quiet', COPY.hide);
    hideBtn.type = 'button';
    hideBtn.hidden = true;
    controls.appendChild(showBtn);
    controls.appendChild(hideBtn);
    // A live region present from wire time (the `.gm-complete-status` precedent): its text
    // changes are announced because the container is already in the accessibility tree.
    var status = el(doc, 'p', 'gmv-status');
    status.setAttribute('aria-live', 'polite');
    var result = el(doc, 'div', 'gmv-result');
    result.id = 'gmv-result';
    result.hidden = true;
    section.appendChild(heading);
    section.appendChild(intro);
    section.appendChild(controls);
    section.appendChild(status);
    section.appendChild(result);

    // Under the step's ✓/✗ rail (the advisory tools sit together); appended otherwise.
    var rail = panel.querySelector('.gm-checks');
    if (rail && rail.parentNode === panel) {
      panel.insertBefore(section, rail.nextSibling);
    } else {
      panel.appendChild(section);
    }

    // ── the run ───────────────────────────────────────────────────────────────────────────
    var hidden = null;
    var running = false;

    function liveMain() {
      try {
        var files = core.getFiles();
        if (Array.isArray(files)) {
          var main = files.filter(function (f) { return f && f.name === 'main.py'; })[0];
          if (main && typeof main.content === 'string') { return main.content; }
        }
      } catch (e) { /* fall back */ }
      try { return core.getSource() || ''; } catch (e2) { return ''; }
    }

    function ensureHidden() {
      if (hidden) { return hidden.ready(); }
      var make = typeof o.createHiddenRunner === 'function' ? o.createHiddenRunner : createHiddenRunner;
      hidden = make({ doc: doc, ace: o.ace, mode: 'python', source: '' });
      return hidden.ready().catch(function (err) {
        // A runner that never came up is dropped, so the next press tries a fresh one.
        try { hidden.destroy(); } catch (e) { /* ignore */ }
        hidden = null;
        throw err;
      });
    }

    function render(view) {
      clear(result);
      status.textContent = '';
      if (!view.ok) {
        // Nothing to show but the reason: the live status line carries it alone.
        result.hidden = true;
        hideBtn.hidden = true;
        showBtn.textContent = COPY.show;
        showBtn.setAttribute('aria-expanded', 'false');
        status.textContent = view.notes[0] || COPY.failed;
        return;
      }
      view.notes.forEach(function (note) {
        result.appendChild(el(doc, 'p', 'gmv-note', note));
      });
      if (view.final.length) {
        result.appendChild(el(doc, 'h3', 'gmv-subheading', COPY.finalHeading));
        result.appendChild(buildTable(doc, [COPY.nameCol, COPY.valueCol], view.final.map(function (f) {
          return [f.name, f.value];
        })));
      }
      if (view.rows.length) {
        var details = el(doc, 'details', 'gmv-changes');
        details.appendChild(el(doc, 'summary', 'gmv-summary', COPY.changesSummary + ' (' + view.rows.length + ')'));
        details.appendChild(buildTable(doc, [COPY.lineCol, COPY.nameCol, COPY.valueCol], view.rows.map(function (r) {
          return [String(r.line), r.name, r.value];
        })));
        result.appendChild(details);
      }
      result.hidden = false;
      hideBtn.hidden = false;
      showBtn.textContent = COPY.refresh;
      showBtn.setAttribute('aria-expanded', 'true');
      // A short summary for screen readers; the tables themselves are read on demand.
      status.textContent = view.final.length
        ? view.final.length + (view.final.length === 1 ? ' variable shown.' : ' variables shown.')
        : '';
    }

    function run() {
      if (running) { return Promise.resolve(null); }
      running = true;
      showBtn.disabled = true;
      section.setAttribute('aria-busy', 'true');
      status.textContent = COPY.running;
      var program = buildTraceProgram(liveMain(), { mode: mode });
      return Promise.resolve().then(ensureHidden).then(function () {
        return hidden.runSource(program, [], RUN_BUDGET_MS);
      }).then(function (res) {
        return describeRun(res, mode);
      }, function () {
        return describeRun(null, mode);
      }).then(function (view) {
        render(view);
        return view;
      }).then(function (view) {
        running = false;
        showBtn.disabled = false;
        section.removeAttribute('aria-busy');
        return view;
      }, function (err) {
        running = false;
        showBtn.disabled = false;
        section.removeAttribute('aria-busy');
        console.error(err);
        return null;
      });
    }

    function hide() {
      clear(result);
      result.hidden = true;
      hideBtn.hidden = true;
      status.textContent = '';
      showBtn.textContent = COPY.show;
      showBtn.setAttribute('aria-expanded', 'false');
      showBtn.focus();
    }

    showBtn.addEventListener('click', function () { run(); });
    hideBtn.addEventListener('click', hide);

    return { run: run, hide: hide, section: section };
  } catch (err) {
    console.error('variable-trace-chrome:', err);
    return null;
  }
}
